import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, StickyFooter } from 'is24-corecss'
import PublicationBarLoader from '../../../../common/display/publicationBarLoader/PublicationBarLoader'
import styles from './PublicationStickyFooter.module.css'

export interface PublicationStickyFooterProps {
  isSaving: boolean
  isNativeMobileView: boolean
  onPreview: () => void
  onSave: () => void
  onSaveForLater: () => Promise<boolean>
  increaseSubmitCount: () => void
  isWaitingListFlow?: boolean
}

export const PublicationStickyFooter = ({
  isSaving,
  isNativeMobileView,
  onPreview,
  onSave,
  onSaveForLater,
  increaseSubmitCount,
  isWaitingListFlow,
}: PublicationStickyFooterProps) => {
  const { t } = useTranslation()

  if (isSaving) {
    return <PublicationBarLoader />
  }
  if (isWaitingListFlow) {
    const webviewPrimaryButton = (
      <div className='align-center'>
        <Button
          id='checkout-listing-type'
          variant='filledDefault'
          // mf is for mouseflow
          className='mf-form-button'
          onClick={() => {
            increaseSubmitCount()
            onSave()
          }}
          disabled={isSaving}
        >
          {t('button.save.waitingList')}
        </Button>
      </div>
    )
    return (
      <StickyFooter.Root align='center' className={`${styles.webviewStickyFooter}`} show>
        <StickyFooter.Actions>{webviewPrimaryButton}</StickyFooter.Actions>
      </StickyFooter.Root>
    )
  }

  if (isNativeMobileView) {
    const webviewPrimaryButton = (
      <div className='one-half'>
        <Button
          id='checkout-listing-type'
          variant='filledDefault'
          // mf is for mouseflow
          className='mf-form-button one-whole'
          onClick={() => {
            // TODO HPEP-1348 tracking
            increaseSubmitCount()
            onSave()
          }}
          disabled={isSaving}
        >
          {t('button.save.short')}
        </Button>
      </div>
    )

    const webviewSecondaryButton = (
      <div className='one-half'>
        <Button
          variant='textDefault'
          className='one-whole'
          onClick={async () => {
            increaseSubmitCount()
            const success = await onSaveForLater()
            if (success) {
              window.location.href = 'is24://retargetShowOffer'
            }
          }}
          disabled={isSaving}
        >
          {t('button.save.saveForLater')}
        </Button>
      </div>
    )

    return (
      <StickyFooter.Root align='center' className={styles.webviewStickyFooter} show>
        <StickyFooter.Actions>
          {webviewSecondaryButton}
          {webviewPrimaryButton}
        </StickyFooter.Actions>
      </StickyFooter.Root>
    )
  }

  const primaryButton = (
    <Button
      id='checkout-listing-type'
      data-cy='checkout-listing-type'
      className='mf-form-button'
      variant='filledDefault'
      onClick={() => {
        increaseSubmitCount()
        onSave()
      }}
      disabled={isSaving}
    >
      {t('button.save.default', 'Publish')}
    </Button>
  )

  const secondaryButton = (
    <Button
      id='preview-listing-type'
      variant='outlineStrong'
      onClick={() => {
        increaseSubmitCount()
        onPreview()
      }}
      disabled={isSaving}
    >
      {t('button.preview', 'Preview')}
    </Button>
  )

  return (
    <StickyFooter.Root align='center' show>
      <StickyFooter.Actions>
        {secondaryButton}
        {primaryButton}
      </StickyFooter.Actions>
    </StickyFooter.Root>
  )
}
